import moment from 'moment';
import type { CalendarLeanEvent } from '../types';
import { buildChipViewModel } from './chip-view-model';

export interface MonthDayCell {
    date: Date;
    inMonth: boolean;
    isToday: boolean;
    /** Chips actually rendered in the cell. */
    visible: CalendarLeanEvent[];
    /** The rest, reachable through the day panel. */
    overflow: CalendarLeanEvent[];
    total: number;
    statusCounts: { sent: number; pending: number; failed: number };
}

export interface MonthLayout {
    weeks: MonthDayCell[][];
    rangeStart: Date;
    rangeEnd: Date;
}

export interface MonthLayoutOptions {
    monthStart: Date;
    weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    maxChipsPerCell: number;
    now: number;
}

const MAX_WEEKS = 6;
const DAYS = 7;

const dayKey = (date: Date | string): string => moment(date).format('YYYY-MM-DD');

/**
 * Bucket the month's events into the weeks the month actually spans.
 *
 * A fixed six-row grid pads September 2026 with a whole week of October, which
 * reads as "next month is already here". The grid renders only the weeks that
 * contain a day of this month — 4, 5 or 6 — so the trailing row is never made
 * of next month alone. Row HEIGHT stays fixed, so the cells keep their size and
 * density is still handled by the visible/overflow split.
 */
export function buildMonthLayout(
    events: CalendarLeanEvent[],
    { monthStart, weekStartsOn, maxChipsPerCell, now }: MonthLayoutOptions,
): MonthLayout {
    const first = moment(monthStart).startOf('month');
    const lead = (first.day() - weekStartsOn + DAYS) % DAYS;
    const gridStart = first.clone().subtract(lead, 'days').startOf('day');

    const byDay = new Map<string, CalendarLeanEvent[]>();
    for (const event of events) {
        const key = dayKey(event.dueDate);
        const bucket = byDay.get(key);
        if (bucket) bucket.push(event);
        else byDay.set(key, [event]);
    }
    byDay.forEach(list =>
        list.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()));

    const todayKey = dayKey(new Date(now));
    const month = first.month();

    // Weeks needed to cover lead-in days + the month itself, capped at six.
    const weekCount = Math.min(
        MAX_WEEKS,
        Math.ceil((lead + first.daysInMonth()) / DAYS),
    );

    const weeks: MonthDayCell[][] = [];

    for (let w = 0; w < weekCount; w++) {
        const week: MonthDayCell[] = [];
        for (let d = 0; d < DAYS; d++) {
            const date = gridStart.clone().add(w * DAYS + d, 'days');
            const key = date.format('YYYY-MM-DD');
            const dayEvents = byDay.get(key) ?? [];

            // Hiding exactly one event behind a summary row costs the user a
            // click for nothing, so the cap only bites from the second hidden
            // item on.
            const showAll = dayEvents.length <= maxChipsPerCell + 1;
            const visible = showAll ? dayEvents : dayEvents.slice(0, maxChipsPerCell);
            const overflow = showAll ? [] : dayEvents.slice(maxChipsPerCell);

            // 'unconfirmed' means it went out without a delivery receipt, so it
            // counts as sent for the day's summary rather than as trouble.
            const statusCounts = { sent: 0, pending: 0, failed: 0 };
            for (const event of dayEvents) {
                const status = buildChipViewModel(event, now).status;
                statusCounts[status === 'unconfirmed' ? 'sent' : status] += 1;
            }

            week.push({
                date: date.toDate(),
                inMonth: date.month() === month,
                isToday: key === todayKey,
                visible,
                overflow,
                total: dayEvents.length,
                statusCounts,
            });
        }
        weeks.push(week);
    }

    return {
        weeks,
        rangeStart: gridStart.toDate(),
        rangeEnd: gridStart.clone().add(weekCount * DAYS, 'days').toDate(),
    };
}
